import {
  ACCESS_ID_QUERY_PARAM,
  SIDEBAR_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
  SecondaryOverlayType,
  TOPIC_ID_QUERY_PARAM,
} from "components/constants";
import { OrgType, SectionPageCategory } from "components/server-types";
import { useLocation } from "utils/routing";

export const BASE_SECTION_URL = "/section/";
export const BASE_SECTION_MONITORING_URL = "/monitoring/section/";
export const MONITORING_URL = "/monitoring";

const HOME_PATH_REGEX = /\/(home)/;
const SECTION_MONITORING_PATH_REGEX = /\/monitoring\/section\/[a-z0-9]*/;
const MONITORING_PATH_REGEX = /\/monitoring/;
const SECTION_VIEW_PATH_REGEX = /\/section\/[a-z0-9]*/;
export const PAGE_PATH_REGEX = /\/section\/[a-z0-9]+\/page\/[a-z0-9]*/;
export const PROJECTOR_PATH_REGEX = /\/section\/[a-z0-9]*\/projector/;

export const enum CONTENT_SIDEBAR_TAB_NAME {
  Content = "Topics",
  Planner = "Planner",
  InstructorNotes = "InstructorNotes",
  AiAssistant = "AiAssistant",
  Users = "Users",
  Profile = "Profile",
  HelpCenter = "HelpCenter",
  Assignment = "Assignment",
  CourseResource = "CourseResource",
  InstructorWorkspace = "InstructorWorkspace",
  ClassChat = "ClassChat",
}

/**
 * ViewLevel is used to set or determine what type of view is shown within the section.
 * Currently, this is used to determine what is shown in the header.
 */
export type ViewLevelType = "home" | "section" | "page" | "monitoring";

export const isMpOrg = (org: OrgType | null | undefined) => {
  return org && org.short_name === "mp";
};

export const isValidUrl = (url: string) => {
  try {
    return Boolean(new URL(url));
  } catch {
    return false;
  }
};

// Sections
// TODO: Rename to just "sectionHomeUrl" to be less ambiguous with the "home" page
export const sectionHomeUrl = (sectionId: string | null) =>
  sectionId ? `${BASE_SECTION_URL}${sectionId}` : BASE_SECTION_URL;

export const sectionMonitoringDashboardUrl = (sectionId?: string) =>
  sectionId ? `${BASE_SECTION_MONITORING_URL}${sectionId}` : BASE_SECTION_MONITORING_URL;

type PageUrlOptionsType = {
  tab?: CONTENT_SIDEBAR_TAB_NAME;
  topicId?: string;
  additionalParams?: Record<string, string> | URLSearchParams | string;
};

// TODO: Turn related logic in `page.ts:navigateToPage`, `assessment.ts:startAssessing`,
// into side-effects of the PageView, so that URLs all behave the same.
/**
 *  Get the canonical URL for a page, with optional setting for what tab the sidebar should be on.
 *
 *  When navigating to a topic page (page linked to a topic via an activity), the sidebar will later default
 *  to that page's topic, even if no topicId is passed.
 *  When navigating to a course resource / assignment, the sidebar will later default to that page's category.
 *
 *  `options.additionalParams` can be any format of search params that will simply be tacked onto the URL.
 *
 *  Note that `pageId` can be an empty string ("") if we want to navigate to the
 *  default page of a topic, based on the topicId in the params
 */
export const pageUrl = (sectionId: string, pageId: string, options: PageUrlOptionsType = {}) => {
  const baseUrl = `${sectionHomeUrl(sectionId)}/page/${pageId}`;
  const query = new URLSearchParams(options.additionalParams || "");

  if (options.tab) {
    query.set(SIDEBAR_QUERY_PARAM, options.tab);
  } else if (options.topicId) {
    query.set(TOPIC_ID_QUERY_PARAM, options.topicId);
  }

  return withQueryParams(baseUrl, query);
};

export const withQueryParams = (
  path: string,
  params: Record<string, string> | URLSearchParams | string,
) => {
  const baseUrl = path || "";
  const query = new URLSearchParams(params);

  const queryString = query.toString();
  return queryString?.length > 1 ? `${baseUrl}?${queryString}` : baseUrl;
};

export const sectionOutcomesUrl = (sectionId: string, outcomeId?: string) => {
  if (outcomeId) {
    return `${sectionHomeUrl(sectionId)}?tab=outcomes&outcome-id=${outcomeId}`;
  }
  return `${sectionHomeUrl(sectionId)}?tab=outcomes`;
};

export type SidebarTabType = keyof typeof CONTENT_SIDEBAR_TAB_NAME;

export const SectionPageCategoryToSidebarTab: Record<SectionPageCategory, SidebarTabType> = {
  assignment: "Assignment",
  course_resource: "CourseResource",
  instructor_workspace: "InstructorWorkspace",
};

export const sectionProjectorUrl = (sectionId: string) => `${sectionHomeUrl(sectionId)}/projector`;

// Dev

export const devSectionUsersUrl = (sectionId: string) => `${sectionHomeUrl(sectionId)}/users`;

// Org

export type HomeRoleType = "student" | "instructor" | "admin" | "section_admin";
export const homeUrl = "/home";
export const homeUrlForRole = (role: HomeRoleType) => `${homeUrl}/${role}`;

export const homeOutcomesUrl = (role: HomeRoleType, outcomeId?: string) => {
  const baseUrl = `${homeUrlForRole(role)}?tab=outcomes`;

  if (outcomeId) {
    return `${baseUrl}&outcome-id=${outcomeId}`;
  }

  return baseUrl;
};

// Hard-coded url paths
// TODO: Add real value
export const ON_BOARDING_GUIDE_URL = "#";
export const INSTRUCTOR_FORUM_USER_GUIDE_URL = "https://minerva-project.gitbook.io/forum";
export const STUDENT_FORUM_USER_GUIDE_URL = "https://minerva-project.gitbook.io/forum/students";
export const RELEASE_NOTES_URL = "https://minerva-project.gitbook.io/forum/release-notes";
export const BULK_IMPORT_GUIDE_URL =
  "https://minerva-project.gitbook.io/forum/managing-programs/bulk-enrollment";

// Overlays that shouldn't be maintained between page changes
export const NON_FOOTER_OVERLAYS = [SecondaryOverlay.AssessmentSummary, SecondaryOverlay.Outcomes];

export const CourseContentTabKeys = [
  "content",
  "outcomes",
  "assessments",
  "class_log",
  "ai_dashboard",
  "publication_history",
] as const;

/**
 * Given a list of IDs and a query/search param to get the current ID from, determine which
 * is the previous/next ID in the list from the current position, and generate to link to them.
 */
export const getNavigationValues = (
  ids: string[],
  { queryParam, currentId }: { queryParam?: string; currentId?: string | null },
) => {
  let currentIndex = currentId ? ids.indexOf(currentId) : -1;
  if (currentIndex === -1) {
    // If not found, assume we're on the first index
    currentIndex = 0;
  }

  const nextId = currentIndex < ids.length - 1 ? ids[currentIndex + 1] : null;
  const prevId = currentIndex > 0 ? ids[currentIndex - 1] : null;

  let nextURL: URL | undefined;
  let prevURL: URL | undefined;

  if (nextId && queryParam) {
    nextURL = new URL(location.href);
    nextURL.searchParams.set(queryParam, nextId);

    if (queryParam !== ACCESS_ID_QUERY_PARAM) {
      // Make sure we delete this
      nextURL.searchParams.delete(ACCESS_ID_QUERY_PARAM);
    }
    nextURL.searchParams.delete(SUBMISSION_ID_QUERY_PARAM);
  }

  if (prevId && queryParam) {
    prevURL = new URL(location.href);
    prevURL.searchParams.set(queryParam, prevId);

    if (queryParam !== ACCESS_ID_QUERY_PARAM) {
      // Make sure we delete this
      prevURL.searchParams.delete(ACCESS_ID_QUERY_PARAM);
    }
    prevURL.searchParams.delete(SUBMISSION_ID_QUERY_PARAM);
  }

  return { nextId, prevId, nextURL, prevURL };
};

export const shouldAddOverlayParam = (overlay: SecondaryOverlayType, isSmallScreen: boolean) => {
  if (isSmallScreen) {
    return false;
  }

  return overlay && !NON_FOOTER_OVERLAYS.includes(overlay);
};

export type ViewLevelFlags = Partial<Record<ViewLevelType, boolean>>;

export const _getViewLevel = (path: string) => {
  if (SECTION_MONITORING_PATH_REGEX.test(path)) {
    return { monitoring: true };
  }
  if (MONITORING_PATH_REGEX.test(path)) {
    return { monitoring: true };
  }
  if (HOME_PATH_REGEX.test(path)) {
    return { home: true };
  }
  if (PAGE_PATH_REGEX.test(path)) {
    return { page: true };
  }
  if (SECTION_VIEW_PATH_REGEX.test(path)) {
    return { section: true };
  }
  // TODO: Lots of tests rely on this returning true when not specifying a path, so we return it by default
  // but preferably tests should mount the right path (or we default in tests) vs. doing it here.
  return { section: true };
};

export const useViewLevel = (): ViewLevelFlags => {
  const location = useLocation();
  const path = location.pathname;
  return _getViewLevel(path);
};

/**
 * Forces a hard reload to clear cache and get fresh assets
 * Uses URL parameter cache busting to ensure fresh content is fetched
 */
export const clearCacheAndReload = () => {
  const url = new URL(window.location.href);
  url.searchParams.set("_t", Date.now().toString());
  window.location.href = url.toString();
};
